import { useContext } from "react";
import {
  CollectionDeleteContext,
  CollectionHandlerContext,
  CollectionIndexContext,
  CollectionDeleteContextValue,
  CollectionHandlerContextValue,
  CollectionIndexContextValue,
  CollectionBookContextValue,
  CollectionBookContext,
} from "./";

/* ===========================================================================
 * HOOK: useCollectionIndexContext
 * =========================================================================== */

/**
 * @function useCollectionIndexContext
 * Retrieves the value of the CollectionIndexContext.
 *
 * This Hook ensures that the calling component is wrapped inside
 * an CollectionIndexProvider. If not, an error is thrown to prevent
 * unintended usage without the required context.
 *
 * @returns {CollectionIndexContextValue} The current context value.
 * @throws {Error} If called outside of an CollectionIndexProvider.
 */
export const useCollectionIndexContext = (): CollectionIndexContextValue => {
  const context = useContext(CollectionIndexContext);
  if (!context) {
    throw new Error(
      "useCollectionIndexContext must be used within an CollectionIndexProvider"
    );
  }
  return context;
};

/* ===========================================================================
 * HOOK: useCollectionHandlerContext
 * =========================================================================== */

/**
 * @function useCollectionHandlerContext
 * Retrieves the value of the CollectionHandlerContext.
 *
 * This Hook ensures that the calling component is wrapped inside
 * an CollectionHandlerProvider. If not, an error is thrown to enforce
 * correct usage and avoid undefined state access.
 *
 * @returns {CollectionHandlerContextValue} The current handler context value.
 * @throws {Error} If called outside of an CollectionHandlerProvider.
 */
export const useCollectionHandlerContext =
  (): CollectionHandlerContextValue => {
    const context = useContext(CollectionHandlerContext);
    if (!context) {
      throw new Error(
        "useCollectionHandlerContext must be used within an CollectionHandlerProvider"
      );
    }
    return context;
  };

/* ===========================================================================
 * HOOK: useCollectionDeleteContext
 * =========================================================================== */

/**
 * @function useCollectionDeleteContext
 * Retrieves the value of the CollectionDeleteContext.
 *
 * This Hook ensures that the calling component is wrapped inside
 * an CollectionDeleteProvider. Attempting to use it outside of its
 * provider will result in an explicit contextual usage error.
 *
 * @returns {CollectionDeleteContextValue} The current delete-context value.
 * @throws {Error} If called outside of an CollectionDeleteProvider.
 */
export const useCollectionDeleteContext = (): CollectionDeleteContextValue => {
  const context = useContext(CollectionDeleteContext);
  if (!context) {
    throw new Error(
      "useCollectionDeleteContext must be used within an CollectionDeleteProvider"
    );
  }
  return context;
};

/* ===========================================================================
 * HOOK: useCollectionBookContext
 * =========================================================================== */

/**
 * @function useCollectionBookContext
 * Retrieves the value of the CollectionBookContext.
 *
 * This Hook ensures that the calling component is wrapped inside
 * an CollectionBookProvider. Attempting to use it outside of its
 * provider will result in an explicit contextual usage error.
 *
 * @returns {CollectionBookContextValue} The current delete-context value.
 * @throws {Error} If called outside of an CollectionBookProvider.
 */
export const useCollectionBookContext = (): CollectionBookContextValue => {
  const context = useContext(CollectionBookContext);
  if (!context) {
    throw new Error(
      "useCollectionBookContext must be used within an CollectionBookProvider"
    );
  }
  return context;
};
